import type { MachineId, TokenSpec } from '../../shared/contract.js';
import { ctxFor } from '../modifier.js';
import type { TokenRuntime } from '../token.js';
import { v3, type Vec3 } from '../vec.js';
import { Cabinet, type CabinetHost, type FieldSolid, type TagTable } from './base.js';

/**
 * PACHINKO (Kugelregen). Drop staked tokens from an aimable launcher through a peg
 * field into pockets.
 *
 * Its tag table is almost empty ON PURPOSE — this cabinet is the proof that the grammar
 * works: [Magnet], [Geist], [Gummi], [Kleber], [Schwer], [Spalt], [Zins] all do their
 * thing here without one line of pachinko-specific code, because tokens are real bodies.
 * Only [Gluecks] needs a declared mapping, because "how wide is a pocket mouth" is an
 * odds-space question that physics alone does not answer.
 */

export interface PachinkoSetzen {
  /** radians from vertical, 0 = straight up the lane */
  angle: number;
  /** 0..1 */
  power: number;
  spec: TokenSpec;
}

const BOARD = {
  halfWidth: 6.2,
  height: 20,
  halfDepth: 0.8,
  laneInner: 4.8,
  launchPos: v3(5.5, 1.5, 0),
  pocketTop: 1.3,
  pocketCatchY: 1.0,
  pegRadius: 0.22,
  pegRows: 7,
  pegRow0: 6,
  pegRowStep: 1.4,
  pegCols: [-5.4, -3.7, -2.0, -0.3, 1.4, 3.1],
  pegColOffset: 0.85,
  ledgeY: 4.6,
} as const;

interface Pocket {
  name: string;
  lo: number;
  hi: number;
  value: number;
  jackpot: boolean;
  /** the two pockets flanking the jackpot bank their near-miss into DER TOPF */
  nearMiss: boolean;
}

/** 7 pockets across the board; the middle one is the TOPF/jackpot mouth. */
function makePockets(): Pocket[] {
  const w = (BOARD.halfWidth * 2) / 7;
  const values = [2, 5, 10, 0, 10, 5, 2];
  return values.map((value, i) => {
    const lo = -BOARD.halfWidth + i * w;
    return {
      name: i === 3 ? 'TOPF' : `p${i}`,
      lo,
      hi: lo + w,
      value: i === 3 ? 25 : value,
      jackpot: i === 3,
      nearMiss: i === 2 || i === 4,
    };
  });
}

export class Pachinko extends Cabinet<PachinkoSetzen> {
  readonly id: MachineId = 'pachinko';
  readonly limitLabel = 'Auszahlschacht-Durchsatz — bei Überlauf platzt die Schale.';
  readonly breakMode = 'tray' as const;

  /** Declared odds mapping. Data. The whole table. */
  readonly tagTable: TagTable = {
    Gluecks: { effect: 'widenMouth', note: 'Taschenmäuler öffnen sich für diese Marke um 20 %.' },
  };

  private pockets = makePockets();
  private solids: FieldSolid[] = [];
  private goldenPegs = 0;
  private bustHoleOpen = false;
  private throughputCap = 18;
  private dropped = 0;

  build(): void {
    const phys = this.host.phys;
    const o = this.origin;
    const W = BOARD.halfWidth;
    const H = BOARD.height;
    const D = BOARD.halfDepth;

    // ---- shell: the cabinet itself. Even a ghost cannot leave the room through this.
    phys.addStatic({ kind: 'box', hx: W + 0.2, hy: 0.2, hz: D + 0.2, pos: this.w(v3(0, -0.4, 0)) }, 'shell', 0.5, 0.1);
    phys.addStatic({ kind: 'box', hx: 0.2, hy: H / 2, hz: D, pos: this.w(v3(-W - 0.2, H / 2, 0)) }, 'shell', 0.2, 0.4);
    phys.addStatic({ kind: 'box', hx: 0.2, hy: H / 2, hz: D, pos: this.w(v3(W + 0.2, H / 2, 0)) }, 'shell', 0.2, 0.4);
    phys.addStatic({ kind: 'box', hx: W + 0.4, hy: H / 2, hz: 0.2, pos: this.w(v3(0, H / 2, -D - 0.2)) }, 'shell', 0.2, 0.2);
    phys.addStatic({ kind: 'box', hx: W + 0.4, hy: H / 2, hz: 0.2, pos: this.w(v3(0, H / 2, D + 0.2)) }, 'shell', 0.2, 0.2);
    // top glass
    phys.addStatic({ kind: 'box', hx: W + 0.4, hy: 0.2, hz: D + 0.4, pos: this.w(v3(0, H + 0.2, 0)) }, 'shell', 0.2, 0.2);
    // Launch stub. Deliberately SHORT: a tall rail scrubs off all the shot's sideways
    // velocity against its face, so the token rides to the apex moving straight up and
    // drops back into the corner pocket, never reaching the peg field. The stub only
    // stops the token falling straight back into the launch corner.
    phys.addStatic(
      { kind: 'box', hx: 0.1, hy: 1.1, hz: D, pos: this.w(v3(BOARD.laneInner - 0.1, 2.1, 0)) },
      'shell',
      0.05,
      0.25,
    );
    // pocket dividers
    for (let i = 1; i < this.pockets.length; i++) {
      const x = this.pockets[i]!.lo;
      phys.addStatic(
        { kind: 'box', hx: 0.06, hy: BOARD.pocketTop / 2, hz: D, pos: this.w(v3(x, BOARD.pocketTop / 2, 0)) },
        'shell',
        0.3,
        0.15,
      );
    }

    // ---- field: phaseable by [Geist], sticky-able by [Kleber]
    for (let r = 0; r < BOARD.pegRows; r++) {
      const y = BOARD.pegRow0 + r * BOARD.pegRowStep;
      const off = r % 2 === 0 ? 0 : BOARD.pegColOffset;
      for (const cx of BOARD.pegCols) {
        const x = cx + off;
        if (x > BOARD.laneInner - 0.5) continue;
        const pos = this.w(v3(x, y, 0));
        phys.addStatic({ kind: 'ball', radius: BOARD.pegRadius, pos }, 'field', 0.15, 0.55);
        this.solids.push({ kind: 'ball', pos, r: BOARD.pegRadius });
      }
    }

    // ---- Ablagen (parking trays): the reason [Spalt]+[Zins] turns this board into a
    // patience game. You stop aiming for pockets and start aiming for ledges, let the
    // tokens ripen, then shake them off in one cascade.
    for (const [lx, hx] of [[-3.2, 2.0], [2.4, 1.0]] as [number, number][]) {
      const base = this.w(v3(lx, BOARD.ledgeY, 0));
      phys.addStatic({ kind: 'box', hx, hy: 0.1, hz: 0.7, pos: base }, 'field', 0.95, 0.02);
      this.solids.push({ kind: 'box', pos: base, h: v3(hx, 0.1, 0.7) });
      // Lips are deliberately low: high enough to catch a falling token, low enough that
      // Rütteln can cascade a ripened tray off in one shake. A tray you cannot empty is
      // a trap, not a decision.
      for (const s of [-1, 1]) {
        const lip = this.w(v3(lx + s * hx, BOARD.ledgeY + 0.22, 0));
        phys.addStatic({ kind: 'box', hx: 0.12, hy: 0.22, hz: 0.7, pos: lip }, 'field', 0.95, 0.02);
        this.solids.push({ kind: 'box', pos: lip, h: v3(0.12, 0.22, 0.7) });
      }
    }
  }

  fieldSolids(): readonly FieldSolid[] {
    return this.solids;
  }

  /** Verb 1: Setzen — launcher angle and power. */
  setzen(p: PachinkoSetzen): void {
    if (this.broken) return;
    const power = clamp(p.power, 0, 1);
    const angle = clamp(p.angle, -0.15, 0.75);
    const speed = 13 + 7 * power;
    const jitter = (this.host.rng.float('pachinko:launch') - 0.5) * 0.12;
    const vel: Vec3 = {
      x: -Math.sin(angle) * speed,
      y: Math.cos(angle) * speed,
      z: jitter,
    };
    const tok = this.host.spawnToken(p.spec, this.id, this.w(BOARD.launchPos), vel);
    this.dropped++;
    this.host.emit({ t: 'drop', token: tok.id, machine: this.id });
    this.host.pipe.fire(
      'onDrop',
      { ...ctxFor(this.id, this.host.tick, tok), api: this.host.api },
      { token: tok.id },
    );
  }

  protected escalate(level: number): void {
    // machines.md: doubling down adds golden pegs AND opens a bust-hole at the centre
    const add = 3;
    const phys = this.host.phys;
    for (let i = 0; i < add; i++) {
      const x = (this.host.rng.float('pachinko:golden') - 0.5) * 8;
      const y = 5.2 + this.host.rng.float('pachinko:golden') * 9.5;
      const pos = this.w(v3(x, y, 0));
      phys.addStatic({ kind: 'ball', radius: BOARD.pegRadius * 1.3, pos }, 'field', 0.1, 0.8);
      this.solids.push({ kind: 'ball', pos, r: BOARD.pegRadius * 1.3 });
      this.goldenPegs++;
    }
    this.bustHoleOpen = true;
    this.throughputCap = Math.max(8, this.throughputCap - 2 * (level - 1));
  }

  tickMachine(dt: number): void {
    this.tickShared(dt);
    if (this.broken) return;

    for (const t of this.host.tokensIn(this.id)) {
      if (t.body < 0 || t.stuck) continue;
      const p = this.host.api.position(t.id);
      if (!p) continue;
      const local = { x: p.x - this.origin.x, y: p.y - this.origin.y, z: p.z - this.origin.z };

      if (local.y < -1.2 || Math.abs(local.x) > BOARD.halfWidth + 1.5) {
        this.host.escaped(t, this, this.escapeMode(t), p);
        continue;
      }
      if (local.y <= BOARD.pocketCatchY) this.land(t, local, p);
    }
    this.checkLimit();
  }

  private land(t: TokenRuntime, local: Vec3, world: Vec3): void {
    const pocket = this.pocketFor(t, local.x);
    if (!pocket) {
      this.registerDud(t);
      this.host.removeToken(t.id, 'lost');
      return;
    }

    if (pocket.jackpot && this.bustHoleOpen) {
      // Nachlegen turned the jackpot mouth into a gamble: the bust hole sits under it.
      const survived = this.host.roll('pachinko', 'busthole', 0.5, t);
      if (!survived) {
        this.host.emit({ t: 'bust', machine: this.id });
        this.registerDud(t);
        this.host.removeToken(t.id, 'lost');
        return;
      }
    }

    if (pocket.nearMiss) {
      // "Almost" is stored value, not manipulation: it visibly feeds DER TOPF.
      this.feedTopf(Math.max(1, Math.round(t.faceValue * 0.5)));
    }

    const base = t.faceValue * pocket.value;
    this.registerScore(t, pocket.name, base, world, pocket.jackpot);
    this.host.removeToken(t.id, 'scored');
  }

  /** [Gluecks] widens the mouth this token sees — the machine's one declared mapping. */
  private pocketFor(t: TokenRuntime, x: number): Pocket | null {
    const rule = this.tagTable.Gluecks;
    let widen = 1;
    if (rule && rule.effect === 'widenMouth') {
      widen = this.host.pipe.query('pocketWidth', ctxFor(this.id, this.host.tick, t), 1);
    }
    let best: Pocket | null = null;
    for (const p of this.pockets) {
      const mid = (p.lo + p.hi) / 2;
      const half = ((p.hi - p.lo) / 2) * (p.jackpot ? widen : 1);
      if (x >= mid - half && x <= mid + half) {
        if (!best || p.jackpot) best = p;
      }
    }
    return best;
  }

  structuralLoad(): number {
    return this.loadWindow.length / this.throughputCap;
  }

  burstValue(): number {
    let v = this.topf;
    for (const t of this.host.tokensIn(this.id)) v += t.faceValue * 3;
    return v + 40 * this.stakeLevel;
  }

  protected override collectRemaining(): { muenzen: number; tickets: number } {
    return { muenzen: 0, tickets: 0 };
  }

  extra(): unknown {
    return {
      pockets: this.pockets.map((p) => ({ name: p.name, lo: p.lo, hi: p.hi, value: p.value })),
      goldenPegs: this.goldenPegs,
      bustHoleOpen: this.bustHoleOpen,
      dropped: this.dropped,
      throughputCap: this.throughputCap,
      launcher: { pos: [BOARD.launchPos.x, BOARD.launchPos.y, BOARD.launchPos.z] },
    };
  }
}

const clamp = (x: number, lo: number, hi: number): number => (x < lo ? lo : x > hi ? hi : x);
